import React from 'react';
import { Card, Row, Col, Statistic, Icon, Tag, Progress, Divider } from 'antd';

const CostBreakdown = ({ recentRequests, userAnalytics, loading }) => {
  // Calculate cost statistics
  const calculateCostStats = () => {
    if (!recentRequests || recentRequests.length === 0) {
      return {
        totalCost: 0,
        avgCostPerRequest: 0,
        costByEndpoint: [],
        topSpenders: []
      };
    }

    let totalCost = 0;
    const endpointCosts = {};
    
    recentRequests.forEach(req => {
      const cost = req.cost || req.cost_estimate || 0;
      totalCost += cost;
      
      const endpoint = req.path || req.endpoint || 'unknown';
      if (!endpointCosts[endpoint]) {
        endpointCosts[endpoint] = { path: endpoint, cost: 0, count: 0 };
      }
      endpointCosts[endpoint].cost += cost;
      endpointCosts[endpoint].count++;
    });
    
    const costByEndpoint = Object.values(endpointCosts)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 5);
    
    const avgCostPerRequest = recentRequests.length > 0 ? totalCost / recentRequests.length : 0;
    
    // Get top spenders from user analytics
    const topSpenders = (userAnalytics || [])
      .filter(user => user.total_cost > 0)
      .sort((a, b) => b.total_cost - a.total_cost)
      .slice(0, 5);

    return {
      totalCost,
      avgCostPerRequest,
      costByEndpoint,
      topSpenders
    };
  };

  const stats = calculateCostStats();
  const maxEndpointCost = stats.costByEndpoint.length > 0 ? stats.costByEndpoint[0].cost : 1;
  const maxUserCost = stats.topSpenders.length > 0 ? stats.topSpenders[0].total_cost : 1;

  const getEndpointName = (path) => {
    if (!path || path === 'unknown') return 'Unknown';
    if (path.includes('/openai/chat/completions')) return 'AI Insights';
    if (path.includes('suggest-questions')) return 'Smart Survey';
    if (path.includes('optimize-order')) return 'Question Ordering';
    if (path.includes('suggest-followups')) return 'Follow-up Suggestions';
    if (path.includes('survey-generation')) return 'Survey Generation';
    const parts = path.split('/').filter(p => p);
    return parts[parts.length - 1] || path;
  };

  return (
    <Card 
      loading={loading}
      style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      title={
        <span>
          <Icon type="dollar" style={{ marginRight: 8, color: '#52c41a' }} />
          Cost Analytics
        </span>
      }
    >
      <Row gutter={[24, 24]}>
        {/* Summary Stats */}
        <Col xs={24} sm={12} md={6}>
          <Card style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>Total Cost</span>}
              value={stats.totalCost}
              precision={3}
              prefix={<span style={{ color: 'white' }}>$</span>}
              valueStyle={{ color: 'white', fontSize: 24 }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>Avg Per Request</span>}
              value={stats.avgCostPerRequest}
              precision={4}
              prefix={<span style={{ color: 'white' }}>$</span>}
              valueStyle={{ color: 'white', fontSize: 24 }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(80,80,80,0.8)' }}>Active Users</span>}
              value={userAnalytics?.length || 0}
              prefix={<Icon type="team" style={{ color: '#595959' }} />}
              valueStyle={{ color: '#595959', fontSize: 24 }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card style={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', border: 'none' }}>
            <Statistic
              title={<span style={{ color: 'rgba(80,80,80,0.8)' }}>Total Requests</span>}
              value={recentRequests?.length || 0}
              prefix={<Icon type="thunderbolt" style={{ color: '#595959' }} />}
              valueStyle={{ color: '#595959', fontSize: 24 }}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row gutter={[24, 24]}>
        {/* Cost by Endpoint */}
        <Col xs={24} lg={12}>
          <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
            <Icon type="api" style={{ marginRight: 8, color: '#1890ff' }} />
            Cost by Endpoint
          </h3>
          {stats.costByEndpoint.length > 0 ? (
            <div>
              {stats.costByEndpoint.map((endpoint, index) => {
                const percentage = (endpoint.cost / maxEndpointCost) * 100;
                return (
                  <div key={endpoint.path} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>
                        {getEndpointName(endpoint.path)}
                      </span>
                      <div>
                        <Tag color="blue" style={{ marginRight: 8 }}>{endpoint.count} req</Tag>
                        <span style={{ fontWeight: 600, color: '#52c41a' }}>
                          ${endpoint.cost.toFixed(3)}
                        </span>
                      </div>
                    </div>
                    <Progress 
                      percent={percentage}
                      strokeColor="#52c41a"
                      showInfo={false}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              <Icon type="inbox" style={{ fontSize: 32, marginBottom: 8 }} />
              <p>No cost data available</p>
            </div>
          )}
        </Col>

        {/* Active Users */}
        <Col xs={24} lg={12}>
          <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
            <Icon type="team" style={{ marginRight: 8, color: '#1890ff' }} />
            Active Users
          </h3>
          {stats.topSpenders.length > 0 ? (
            <div>
              {stats.topSpenders.map((user, index) => {
                const percentage = (user.total_cost / maxUserCost) * 100;
                const isEmail = user.user_id && user.user_id.includes('@');
                
                return (
                  <div key={user.user_id} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {index === 0 && <Icon type="trophy" style={{ color: '#faad14', marginRight: 4 }} />}
                        {index === 1 && <Icon type="trophy" style={{ color: '#d9d9d9', marginRight: 4 }} />}
                        {index === 2 && <Icon type="trophy" style={{ color: '#d48806', marginRight: 4 }} />}
                        <Icon type={isEmail ? 'mail' : 'user'} style={{ marginRight: 4, color: '#1890ff' }} />
                        <span style={{ fontSize: 14, fontWeight: 500 }}>
                          {user.user_id}
                        </span>
                      </div>
                      <div>
                        <Tag color="blue" style={{ marginRight: 8 }}>{user.total_requests} req</Tag>
                        <span style={{ fontWeight: 600, color: '#52c41a' }}>
                          ${user.total_cost.toFixed(3)}
                        </span>
                      </div>
                    </div>
                    <Progress 
                      percent={percentage}
                      strokeColor="#1890ff"
                      showInfo={false}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              <Icon type="inbox" style={{ fontSize: 32, marginBottom: 8 }} />
              <p>No user cost data available</p>
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default CostBreakdown;
